import React from 'react';
import { ArrowRightIcon, UsersIcon } from 'lucide-react';
import { PayeeSummary } from '../../types/commission';
import { payeeStoredTotal } from '../../utils/commission';
import { formatUsd, sumCents } from '../../utils/format';
import { TL_UNIT_COUNT_REVIEW_NOTE } from '../../data/periodClose';
import { Badge } from '../sf/Badge';
import { NewBadge } from '../sf/NewBadge';
import { Tooltip } from '../sf/Tooltip';
import { Card } from '../sf/Card';
import { Td, Th } from '../sf/DataTable';
import { ExampleDataBadge } from '../sf/ExampleDataBadge';
import { Money } from '../sf/Money';
import { StoredReference } from '../sf/StoredReference';
import { UnderReviewBadge } from '../sf/UnderReviewBadge';
interface PayeeTableProps {
  payees: PayeeSummary[];
  onViewDashboard: () => void;
}
const dash = <span className="text-sf-weak" data-id="element-731" data-mp="1ruf19qpf2p9j">—</span>;
export function PayeeTable({
  payees,
  onViewDashboard
}: PayeeTableProps) {
  const sum = (pick: (payee: PayeeSummary) => number) => sumCents(payees.map(pick));
  const totals = {
    units: payees.reduce((count, payee) => count + payee.units, 0),
    volume: sum(p => p.volume),
    base: sum(p => p.base),
    bonus: sum(p => p.bonus),
    overrides: sum(p => p.overridesAdjusted),
    manual: sum(p => p.manual),
    stored: sum(payeeStoredTotal),
    adjusted: sum(p => p.adjustedTotal)
  };
  return <Card title={`Payouts by payee (${payees.length})`} icon={UsersIcon} iconBg="#0176D3" meta="Each total is the sum of that payee’s commission lines. Adjusted pays; Stored (unadjusted) is reference only." bodyClassName="" actions={<ExampleDataBadge data-id="element-733" data-mp="1io8bid8edltu" />} data-id="element-732" data-mp="1rh1w8q6m5df3">
      <div className="overflow-x-auto" data-id="element-734" data-mp="1mqbstihqzkn1">
        <table className="w-full border-collapse" data-id="element-735" data-mp="1mpa5aejjp5of">
          <thead data-id="element-736" data-mp="1mnz1ieq3nxxb">
            <tr data-id="element-737" data-mp="2ag62jr6rst32">
              <Th data-id="element-738" data-mp="2afnw61qe8yhf">Payee</Th>
              <Th data-id="element-739" data-mp="2aecx2f7w8ewn">Branch</Th>
              <Th align="right" data-id="element-740" data-mp="2ae31mc42xfjk">Units</Th>
              <Th align="right" data-id="element-741" data-mp="2f6wbfeeene2i">Volume</Th>
              <Th align="right" className="font-normal" data-id="element-742" data-mp="2f8qu0m27eu3f">
                Base
                <span className="block text-[11px] text-sf-weak" data-id="element-743" data-mp="2p235aevov2hl">stored ref.</span>
              </Th>
              <Th align="right" className="font-normal" data-id="element-744" data-mp="2l1j6l6vgvqtc">
                Bonus
                <span className="block text-[11px] text-sf-weak" data-id="element-745" data-mp="2g0vk8yi6ny5g">stored ref.</span>
              </Th>
              <Th align="right" data-id="element-746" data-mp="2b1sf7e27drxh">
                TL / Director
                <span className="block text-[11px] font-normal text-sf-weak" data-id="element-747" data-mp="263ihavp3gd86">adjusted</span>
              </Th>
              <Th align="right" data-id="element-748" data-mp="21ciljtrpo179">Manual & Adj.</Th>
              <Th align="right" data-id="element-749" data-mp="21br1ng7h7rwt">
                Adjusted
                <span className="block text-[11px] font-normal text-sf-weak" data-id="element-750" data-mp="1xanm6utcbxw4">adj. loan × BPS · pays</span>
              </Th>
              <Th align="right" className="font-normal" data-id="element-751" data-mp="1sdngwm4bez62">
                Stored (unadjusted)
                <span className="block text-[11px] text-sf-weak" data-id="element-752" data-mp="1nby2cwhvw3d8">reference</span>
              </Th>
              <Th data-id="element-753" data-mp="1ifhbvvn7pcwz">
                <span className="sr-only" data-id="element-754" data-mp="1iey4v9z3o8xq">Dashboard</span>
              </Th>
            </tr>
          </thead>
          <tbody data-id="element-755" data-mp="18cc0z5ewz9lv">
            {payees.map(payee => <tr key={payee.id} data-id="element-756" data-mp="1d8aybii801yf">
                <Td className="whitespace-nowrap" data-id="element-757" data-mp="1dbf8qxma3txj">
                  <span className="flex items-center gap-2" data-id="element-758" data-mp="1i85mheam2dfs">
                    <span className="font-semibold" data-id="element-759" data-mp="1i6kv1xjlfam5">{payee.name}</span>
                    {payee.role !== 'Loan Officer' && <Badge tone="info" data-id="element-760" data-mp="pjrp90rcjl5a">{payee.role}</Badge>}
                  </span>
                  {payee.note && <span className="flex items-center gap-1.5 text-xs text-sf-weak" data-id="element-761" data-mp="uibdj7tex90t">
                      {payee.note}
                      {payee.noteIsNew && <NewBadge label="NEW" data-id="element-762" data-mp="zgcwztur3act" />}
                    </span>}
                  {payee.role !== 'Loan Officer' && <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-sf-weak" data-id="element-763" data-mp="6ti9sp6jtwvn">
                      Bonus: none on TL / Director row
                      <Tooltip content={TL_UNIT_COUNT_REVIEW_NOTE} align="start" data-id="element-764" data-mp="bqiil7us9dqg">
                        <UnderReviewBadge data-id="element-765" data-mp="2diami1bu71ai" />
                      </Tooltip>
                    </span>}
                </Td>
                <Td className="whitespace-nowrap text-sf-weak" data-id="element-766" data-mp="28gl8qdx8c9iq">{payee.branch}</Td>
                <Td align="right" data-id="element-767" data-mp="2n8n5vq7x0of2">{payee.units || dash}</Td>
                <Td align="right" data-id="element-768" data-mp="2igc5ynd6d2mi">{payee.volume ? formatUsd(payee.volume) : dash}</Td>
                <Td align="right" data-id="element-769" data-mp="2w7h57ocmrswb">{payee.base ? <StoredReference value={payee.base} compact data-id="element-770" data-mp="2w6pdwpq2ghbu" /> : dash}</Td>
                <Td align="right" data-id="element-771" data-mp="2s76ophr7pk37">{payee.bonus ? <StoredReference value={payee.bonus} compact data-id="element-772" data-mp="2s5vkxhxrocc3" /> : dash}</Td>
                <Td align="right" data-id="element-773" data-mp="36554mrl6lokt">
                  {payee.overridesAdjusted ? <>
                      <Money value={payee.overridesAdjusted} data-id="element-774" data-mp="318em7xlwmgw8" />
                      <span className="block" data-id="element-775" data-mp="1brx72s5tmyvh">
                        <StoredReference value={payee.overrides} compact data-id="element-776" data-mp="1bsox7ngng4ed" />
                      </span>
                    </> : dash}
                </Td>
                <Td align="right" data-id="element-777" data-mp="3iiik71v4pr9w">{payee.manual ? <Money value={payee.manual} signed data-id="element-778" data-mp="3nf8z3lv76gtq" /> : dash}</Td>
                <Td align="right" className="font-bold" data-id="element-779" data-mp="3nhldxq5nsb2k">
                  <Money value={payee.adjustedTotal} data-id="element-780" data-mp="38pkykmuubm72" />
                </Td>
                <Td align="right" data-id="element-781" data-mp="3dpflkfbhvrmu">
                  <StoredReference value={payeeStoredTotal(payee)} compact data-id="element-782" data-mp="3doeboe67tb6p" />
                </Td>
                <Td data-id="element-783" data-mp="2zp4x1ixnqp4u">
                  <button type="button" onClick={onViewDashboard} className="inline-flex items-center gap-1 whitespace-nowrap rounded text-[13px] text-sf-blue hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sf-blue" data-id="element-784" data-mp="2zoms7xwxj2lb">
                    Dashboard <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-785" data-mp="o0n74xdkyrtk" />
                  </button>
                </Td>
              </tr>)}
            <tr className="bg-[#FAFAF9] font-semibold" data-id="element-786" data-mp="syowg01lnndh">
              <Td colSpan={2} data-id="element-787" data-mp="rikvkye5qtgr">Period total</Td>
              <Td align="right" data-id="element-788" data-mp="rg8702dw9kuq">{totals.units}</Td>
              <Td align="right" data-id="element-789" data-mp="miyhkmqi996w">{formatUsd(totals.volume)}</Td>
              <Td align="right" data-id="element-790" data-mp="hk54uwj1lm7s">
                <StoredReference value={totals.base} compact data-id="element-791" data-mp="hjdatq53fwjm" />
              </Td>
              <Td align="right" data-id="element-792" data-mp="ditf8r0650zl">
                <StoredReference value={totals.bonus} compact data-id="element-793" data-mp="djuz9kz5wxtg" />
              </Td>
              <Td align="right" data-id="element-794" data-mp="1a92bu3hszwl8">
                <Money value={totals.overrides} data-id="element-795" data-mp="1a80zlvp1x41x" />
              </Td>
              <Td align="right" data-id="element-796" data-mp="15ar17qjopi1y">
                <Money value={totals.manual} signed data-id="element-797" data-mp="15ahk6sgmal8e" />
              </Td>
              <Td align="right" className="text-[14px] font-bold" data-id="element-798" data-mp="10hxzlbjiipu3">
                <Money value={totals.adjusted} data-id="element-799" data-mp="whnoxlkrzb8w" />
              </Td>
              <Td align="right" data-id="element-800" data-mp="wfbb9km1vn1n">
                <StoredReference value={totals.stored} compact data-id="element-801" data-mp="1sztslq44ep9n" />
              </Td>
              <Td data-id="element-802" data-mp="1o8lkcxej19y2" />
            </tr>
          </tbody>
        </table>
      </div>
    </Card>;
}